import { EGRESO_VARIANT } from "../../hooks/useEgresoStatus";
import { InfoIcon, ArrowOnRectangle, BookIcon } from "../../components/icons/SvgIcons";
import Button from "../../components/button";

// ---------------------------------------------------------------------------
// Subcomponentes internos
// ---------------------------------------------------------------------------

// Reuse shared icons from SvgIcons.jsx

// ---------------------------------------------------------------------------
// Contenedor base de la sección
// ---------------------------------------------------------------------------

function SectionCard({ children }) {
  return (
    <div className="sm:gap-4 bg-white rounded-[8px] border border-[#61ABCF] divide-y divide-[#61ABCF]">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variantes
// ---------------------------------------------------------------------------

function EgresoBloqueado({ onInfo }) {
  return (
    <SectionCard>
      <div className="py-2 grid grid-cols-2 px-4 items-center">
        <dt className="flex text-sm/6 font-bold text-[#036395] font-inter gap-1">
          EGRESO
          <InfoIcon className="size-6" onClick={onInfo} ariaLabel="Información sobre egreso" />
        </dt>
        <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
          <Button size="sm" disabled icon={<ArrowOnRectangle className="size-4" />}>
            Egresar
          </Button>
        </dd>
      </div>
      <div className="py-2 grid px-4">
        <dt className="text-sm/6 font-medium text-gray-900">
          Este vehículo aún no se puede egresar
        </dt>
      </div>
    </SectionCard>
  );
}

function EgresoSinInfo({ onInfo, onEgresar }) {
  return (
    <SectionCard>
      <div className="py-2 grid grid-cols-2 px-4 items-center">
        <dt className="flex text-sm/6 font-bold text-[#036395] font-inter gap-1">
          EGRESO
          <InfoIcon className="size-6" onClick={onInfo} ariaLabel="Información sobre egreso" />
        </dt>
        <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
            <Button size="sm" onClick={onEgresar} icon={<ArrowOnRectangle className="size-4" />}>
              Egresar
            </Button>
        </dd>
      </div>
      <div className="py-2 grid px-4">
        <dt className="text-sm/6 font-medium text-gray-900">
          No hay información de juzgado sobre si este vehículo se puede egresar,
          el egreso queda a criterio de corralón
        </dt>
      </div>
    </SectionCard>
  );
}

function EgresoPuede({ onInfo, onEgresar, levantamiento, onVerArchivo }) {
  return (
    <SectionCard>
      <div className="py-2 grid grid-cols-2 px-4 items-center">
        <dt className="flex text-sm/6 font-bold text-[#036395] font-inter gap-1">
          EGRESO
          <InfoIcon className="size-6" onClick={onInfo} ariaLabel="Información sobre egreso" />
        </dt>
        <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
            <Button size="sm" onClick={onEgresar} icon={<ArrowOnRectangle className="size-4" />}>
              Egresar
            </Button>
        </dd>
      </div>
      <div className="py-2 grid grid-cols-2 px-4">
        <dt className="text-sm/6 font-medium text-gray-900">Observacion</dt>
        <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
          {levantamiento?.observacion}
        </dd>
      </div>
      <div className="py-2 grid grid-cols-2 px-4">
        <dt className="text-sm/6 font-medium text-gray-900">Retira</dt>
        <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
          {levantamiento?.retira}
        </dd>
      </div>
      {levantamiento?.levantamientoURL && (
        <div className="py-2 grid grid-cols-2 px-4">
          <dt className="text-sm/6 font-medium text-gray-900 col-start-1 flex items-center">
            URL
          </dt>
          <dd className="text-sm/6 text-gray-700 justify-between flex items-center">
            <Button
              size="xs"
              onClick={() => onVerArchivo(levantamiento.levantamientoURL)}
            >
              Ver archivo
            </Button>
          </dd>
        </div>
      )}
    </SectionCard>
  );
}

function EgresoYaEgresado({ onInfo, onVerEgreso, levantamiento, onVerArchivo }) {
  return (
    <SectionCard>
      <div className="py-2 grid grid-cols-2 px-4 items-center">
        <dt className="flex text-sm/6 font-bold text-[#036395] font-inter gap-1">
          EGRESADO
          <InfoIcon className="size-6" onClick={onInfo} ariaLabel="Información sobre egreso" />
        </dt>
        <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
          <Button size="sm" onClick={onVerEgreso} icon={<BookIcon />}>
            Ver egreso
          </Button>
        </dd>
      </div>
      {levantamiento?.levantamientoURL && (
        <div className="py-2 grid grid-cols-2 px-4">
          <dt className="text-sm/6 font-medium text-gray-900 col-start-1 flex items-center">
            URL
          </dt>
          <dd className="text-sm/6 text-gray-700 justify-between flex items-center">
            <Button
              size="xs"
              onClick={() => onVerArchivo(levantamiento.levantamientoURL)}
            >
              Ver archivo
            </Button>
          </dd>
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Componente principal exportado
// ---------------------------------------------------------------------------

/**
 * Renderiza la sección de egreso en DetailComponent.
 * La variante a mostrar se determina externamente con useEgresoStatus.
 *
 * @param {object} props
 * @param {string}   props.variant      - Una de las constantes EGRESO_VARIANT
 * @param {object}   props.levantamiento - Objeto levantamiento (puede ser null)
 * @param {function} props.onInfo        - Abre el modal informativo de egreso
 * @param {function} props.onEgresar     - Abre el modal de egreso
 * @param {function} props.onVerEgreso   - Abre el modal de ver egreso
 * @param {function} props.onVerArchivo  - Abre una URL en nueva pestaña
 */
function EgresoSection({
  variant,
  levantamiento,
  onInfo,
  onEgresar,
  onVerEgreso,
  onVerArchivo,
}) {
  switch (variant) {
    case EGRESO_VARIANT.OCULTO:
      return null;

    case EGRESO_VARIANT.BLOQUEADO:
      return <EgresoBloqueado onInfo={onInfo} />;

    case EGRESO_VARIANT.SIN_INFO:
      return <EgresoSinInfo onInfo={onInfo} onEgresar={onEgresar} />;

    case EGRESO_VARIANT.PUEDE_EGRESAR:
      return (
        <EgresoPuede
          onInfo={onInfo}
          onEgresar={onEgresar}
          levantamiento={levantamiento}
          onVerArchivo={onVerArchivo}
        />
      );

    case EGRESO_VARIANT.YA_EGRESADO:
      return (
        <EgresoYaEgresado
          onInfo={onInfo}
          onVerEgreso={onVerEgreso}
          levantamiento={levantamiento}
          onVerArchivo={onVerArchivo}
        />
      );

    default:
      return null;
  }
}

export default EgresoSection;